import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { concat, EMPTY, firstValueFrom, Observable } from 'rxjs';
import { ClientConfiguration } from '../models/client-configuration';
import { ApiService } from './api.service';
import { ConfigService } from './config.service';
import { environment } from 'src/environments/environment';
import { LoggerService } from './logger.service';
import { LocaleService } from './locale.service';
import { ZendeskService } from '../integrations/zendesk/zendesk.service';
import { StorageService } from './storage.service';
import { FingerprintService } from './fingerprint.service';
import { AuthService } from './auth.service';
import { UserFacade } from '../_core/state/user/user.facade';
import { UserConfigFacade } from '../state/user-config/user-config.facade';
import { UserService } from './user.service';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class InitService {
  private readonly userFacade = inject(UserFacade);
  private readonly userConfigFacade = inject(UserConfigFacade);
  private readonly userService = inject(UserService);
  private readonly zendeskService = inject(ZendeskService);

  constructor(
    private logger: LoggerService,
    private apiService: ApiService,
    private storageService: StorageService,
    private localeService: LocaleService,
    private configService: ConfigService,
    private authService: AuthService,
    private fingerprintService: FingerprintService
  ) {
    if (environment.disableCache) {
      this.logger.warn('HTTP cache disabled!');
    }
  }

  async getConfiguration(): Promise<ClientConfiguration> {
    let config: ClientConfiguration;
    const userId = localStorage.getItem('userId');

    try {
      if (userId) {
        const user = await firstValueFrom(this.userFacade.getUser(userId));

        if (window.birdieApi && user.email) {
          window.birdieApi.update({ contact: { email: user.email } });
        }
      }

      const patronId = this.userFacade.user?.id ?? null;

      await this.localeService.init();
      config = await firstValueFrom(this.apiService.getClientConfiguration(this.localeService.language, patronId));
      await this.processConfiguration(config);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.authService.localLogout();
        this.userService.resetUserData();

        await this.localeService.init();
        config = await firstValueFrom(this.apiService.getClientConfiguration(this.localeService.language));
        await this.processConfiguration(config);
      } else {
        this.configService.initError = true;
        throw error;
      }
    }

    if (this.authService.isAuthenticated()) {
      await firstValueFrom(this.userConfigFacade.getUserConfig());
    }

    return config;
  }

  private async processConfiguration(config: ClientConfiguration) {
    if (config === null) {
      return;
    }

    this.setupCoreServices(config);
    if (config.deviceControlViaWeb) {
      await this.fingerprintService.init();
    }

    localStorage.setItem('clientCode', config.codCliente);

    concat(this.loadTranslationsFromVertical(config), this.loadTranslationsFromClientCode(config)).subscribe();

    // TODO: Move this to app component
    if (
      this.configService.isCeibal() &&
      !localStorage.getItem('userId') &&
      !window.location.href.includes('login/callback')
    ) {
      this.checkCeibalCasSession();
    }
  }

  private setupCoreServices(config: ClientConfiguration) {
    this.configService.init(config);
    this.configService.devMode = !environment.production;
    this.storageService.init(config.useSessionStorage);
    this.zendeskService.init(this.localeService.language);
  }

  private loadTranslationsFromVertical(config: ClientConfiguration): Observable<Record<string, string>> {
    const customerType = config.customerType.dbValue.toLowerCase();

    if (customerType !== 'default') {
      return this.localeService.loadExternalTranslation(
        `${environment.translationsBaseUrl}opac/${customerType}/${this.localeService.language}.json`
      ).pipe(
        catchError(() => {
          console.warn(
            `Vertical '${customerType}' does not have custom translations for '${this.localeService.language}' language.\nTried loading them from ${environment.translationsBaseUrl}opac/${customerType}/${this.localeService.language}.json`
          );
          return EMPTY;
        })
      );
    }
    return EMPTY;
  }

  private loadTranslationsFromClientCode(config: ClientConfiguration): Observable<Record<string, string>> {
    const clientCode = config.codCliente;
    return this.localeService
      .loadExternalTranslation(
        `${environment.translationsBaseUrl}opac/customers/${clientCode.toLowerCase()}/${this.localeService.language}.json`
      )
      .pipe(
        catchError(() => {
          console.debug(
            `Client '${clientCode}' does not have custom translations for '${this.localeService.language}' language.\nTried loading them from ${environment.translationsBaseUrl}opac/customers/${clientCode.toLowerCase()}/${this.localeService.language}.json`
          );
          return EMPTY;
        })
      );
  }

  checkCeibalCasSession() {
    this.apiService.redirectToCastLogin().subscribe({
      next: (data) => {
        if (data.url.startsWith('https://bibliotecapais.ceibal.edu.uy')) {
          window.location.href = 'https://bibliotecapais.ceibal.edu.uy/opac/authCAS?app=opac';
        }
      },
      error: (err) => {
        // TODO: We have to remove this error handling. Because it's sloppy. We must correctly configure our cloudfront.
        window.location.href = 'https://bibliotecapais.ceibal.edu.uy/opac/authCAS?app=opac';
      },
    });
  }
}
